import {
  IconRoute,
  IconTransferIn,
  IconTransferOut,
} from '@tabler/icons-react';
import { PlanMarket } from '@zorro/clients';
import { BadgeColor } from '@zorro/design-system/v2/Badge';
import { useRoles } from '@zorro/web/ui-utils';
import { $enum } from 'ts-enum-util';

import { PlanBadge } from '../PlanBadge';
import classNames from './PlanMarketBadge.module.scss';

export const getPlanMarketIconLabelConfig = (
  planMarket: PlanMarket,
  isEmployeeOnlyRole = true
) => {
  return $enum.mapValue(planMarket).with({
    [PlanMarket.ON_EXCHANGE]: {
      icon: <IconTransferIn className={classNames.onExchange} />,
      circleIcon: (
        <span className={`${classNames.iconCircle} ${classNames.onExchange}`}>
          <IconTransferIn />
        </span>
      ),
      label: 'On Exchange',
      color: 'green' as BadgeColor,
      tooltipLabel: isEmployeeOnlyRole
        ? 'Plans available through the official Marketplace. Any costs above your allowance are deducted post-tax. Note: selecting these plans requires mandatory identity verification'
        : "Plan obtained through the official Marketplace. Costs above the employee's allowance are deducted post-tax. Requires identity verification by the employee",
    },
    [PlanMarket.OFF_EXCHANGE]: {
      icon: <IconTransferOut className={classNames.offExchange} />,
      circleIcon: (
        <span className={`${classNames.iconCircle} ${classNames.offExchange}`}>
          <IconTransferOut />
        </span>
      ),
      label: 'Off Exchange',
      color: 'blue' as BadgeColor,
      tooltipLabel: isEmployeeOnlyRole
        ? 'Plans available directly through insurance carriers. Any costs above your allowance are deducted pre-tax'
        : "Plan obtained directly through the insurance carrier. Costs above the employee's allowance are deducted pre-tax",
    },
    [PlanMarket.BOTH_MARKETS]: {
      icon: <IconRoute className={classNames.bothMarkets} />,
      circleIcon: (
        <span className={`${classNames.iconCircle} ${classNames.bothMarkets}`}>
          <IconRoute />
        </span>
      ),
      label: 'Both Markets',
      color: 'indigo' as BadgeColor,
      tooltipLabel: isEmployeeOnlyRole
        ? 'Plans available through both the official Marketplace and directly from the carrier. We make a best-effort attempt to enroll you off-exchange (pre-tax), otherwise enrollment will be on-exchange (post-tax)'
        : 'Available through both the Marketplace and directly from the carrier. Best-effort enrollment off-exchange (pre-tax); falls back to on-exchange (post-tax) if needed',
    },
  });
};

type Props = {
  planMarket: PlanMarket | null | undefined;
  withIcon?: boolean;
  withCircleIcon?: boolean;
  withColor?: boolean;
  size?: 'xs' | 'sm' | 'md';
};

export const PlanMarketBadge = ({
  planMarket,
  withIcon,
  withCircleIcon,
  withColor,
  size = 'sm',
}: Props) => {
  const { isEmployeeOnlyRole } = useRoles();
  if (!planMarket) {
    return null;
  }

  const { label, tooltipLabel, icon, circleIcon, color } =
    getPlanMarketIconLabelConfig(planMarket, isEmployeeOnlyRole);

  const leftIcon = withCircleIcon ? circleIcon : withIcon ? icon : undefined;

  return (
    <PlanBadge
      tooltipLabel={tooltipLabel}
      leftSide={leftIcon}
      label={label}
      size={size}
      color={withColor ? color : undefined}
    />
  );
};
